import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, retry, throwError, timer } from 'rxjs';
import { ErrorService } from './error.service';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private maxRetries = 1;
  private delayMs = 750;
  private ignoreRetryForHttpStatus = [400, 401, 403];

  private ignoreHttpStatusForUrlBeginningBy = new Set<string>();

  private readonly errorService = inject(ErrorService);

  public addIgnoreHttpStatusForUrlBeginningBy(
    status: number,
    method: string,
    prefixUrl: string,
  ): void {
    const key = `${status}-${method.toUpperCase()}-${prefixUrl}`;
    if (!this.ignoreHttpStatusForUrlBeginningBy.has(key)) {
      this.ignoreHttpStatusForUrlBeginningBy.add(key);
    }
  }

  ignoreStatusAndUrlHavingPrefix(status: number, method: string, url: string): boolean {
    const normalizedMethod = method.toUpperCase();
    const searchedKey = `${status}-${normalizedMethod}-${url}`;
    const searchedWildcardMethodKey = `${status}-*-${url}`;
    return Array.from(this.ignoreHttpStatusForUrlBeginningBy.keys()).some(
      (prefix) => searchedKey.startsWith(prefix) || searchedWildcardMethodKey.startsWith(prefix),
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let error: any = new Error();
    return next.handle(req).pipe(
      retry({
        count: this.maxRetries,
        delay: (retryError) => {
          if (
            retryError instanceof HttpErrorResponse &&
            this.ignoreStatusAndUrlHavingPrefix(retryError.status, req.method, retryError.url || '')
          ) {
            return throwError(() => retryError);
          }
          if (
            retryError instanceof HttpErrorResponse &&
            !this.ignoreRetryForHttpStatus.includes(retryError.status) &&
            req.method !== 'POST'
          ) {
            return timer(this.delayMs);
          }
          return throwError(() => retryError);
        },
      }),

      catchError((err: HttpErrorResponse) => {
        if (this.ignoreStatusAndUrlHavingPrefix(err.status, req.method, err.url || '')) {
          return throwError(() => err);
        }

        let errorMessage = '';
        if (err?.status == 0) {
          errorMessage = 'Le serveur ne répond pas, merci de réessayer plus tard';
          error = new Error(errorMessage);
        } else {
          console.error('HTTP Error:', err);
          error = {
            // Prefer a message provided by the backend payload, fall back to err.message or status
            status: err.status,
            title: err.error?.title,
            message: this.buildMessageFromErrorResponse(err),
            errors: err.error?.errors,
            _links: err.error?._links || {
              self: {
                href: err.url,
              },
            },
            _embedded: {
              errors: err.error?._embedded?.errors || err.error?.errors || [err],
            },
          };
        }
        this.errorService.makeErrorMessage(error);
        return throwError(() => error);
      }),
    );
  }

  buildMessageFromErrorResponse(err: HttpErrorResponse): string {
    const backendMessage =
      err.error?.message || err.error?.title || err.error?.messageDescription || err.error || null;
    if (backendMessage) {
      return backendMessage;
    }
    switch (err.status) {
      case 0:
        return 'Le serveur ne répond pas, merci de réessayer plus tard';
      case 413:
        return 'Le fichier est trop volumineux pour être traité par le serveur (max 4 Mo)';
      default:
        return err.message ?? `HTTP ${err.status}`;
    }
  }
}
